import { Injectable, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ServiceResponse } from 'src/app/@shared/interfaces/service-response.interface';
import { RoutePlanning } from 'src/app/modules/route-planner/interfaces/route-planning/@route-planning.interface';
import { GetRouteRequest } from '../../interfaces/route-planning/service/@route-planning-service.interface';
import {
  TIME_UNITS,
  eTimeUnits,
} from 'src/app/@shared/constants/time-units.constants';
import { ComponentType } from '@angular/cdk/portal';
import { RoutePlanningForm } from '../../interfaces/route-planning/forms/route-planning-form';
import { RoutePlanningRepository } from '../../repositories/route-planning.repository';
import { eDepartureTimeOptions } from '../../constants/departure-time-options.constants';
import { DateTime } from 'src/app/@shared/interfaces/date-time.interface';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  ARABIC,
  DAYS,
  ENGLISH,
  HEBREW,
  HOURS,
  MINUTES,
} from '../../constants/multi-language.constants';
import { AgencyContactInfo } from '../../interfaces/route-planning/agency-contact-info.interface';
import { Address } from '../../interfaces/location/address.interface';
import { City } from '../../interfaces/location/city.interface';
import { Stop } from '../../interfaces/route-planning/gtfs/stop.interface';
import { Route } from '../../interfaces/route-planning/gtfs/route.interface';
import { CarIntegratedForm } from '../../interfaces/route-planning/forms/car-integrated-form';
import { eTransportationOptions } from '../../constants/transportation-options.constants';
import { SourceDestinationForm } from '../../interfaces/route-planning/forms/source-destination-form';
import { BaseMap } from '../../components/shared/map/base-map.component';
import { PointOfInterest } from '../../interfaces/location/point-of-interest.interface';
import { MapService } from '../map/map.service';
import { RouteTrackingService } from '../../services/route-tracking/route-tracking.service';
import { eTab } from '../../enums/route-planning/tabs/tabs.enums';
import { eLegOptions } from '../../constants/leg-options.constants';

export interface ChangeRoutePlanning {
  formValues: RoutePlanningForm;
  results: any[];
  resubmittedWithoutFilters?: boolean;
  leg?: Address;
}

@Injectable({
  providedIn: 'root',
})
export class RoutePlanningService implements OnInit {
  _formSubject = new BehaviorSubject<ChangeRoutePlanning>(null);
  _resultSubject = new BehaviorSubject<{
    type: 'selected' | 'highlighted';
    value: any;
  }>({ type: 'selected', value: null });

  _componentOverlaySub = new Subject<{
    componentType: ComponentType<any>;
    data?: {
      labelQueryParam?: string;
      closeCallbackFn?: (value?: any) => void;
    } & {
      [key: string]: any;
    };
  }>();

  _mapOverlaySub = new Subject<{
    mapComponent: ComponentType<BaseMap<any>>;
    data?: {
      closeCallbackFn?: (value?: any, target?: 'origin' | 'destination' | 'stop') => void;
    } & {
      [key: string]: any;
    };
  }>();

  /** Emit to update search-line map data without closing the overlay (line, boardingStation, dropingStation). */
  searchLineMapData$ = new BehaviorSubject<{
    line: Route;
    boardingStation: Stop | null;
    dropingStation: Stop | null;
  } | null>(null);

  _trackingRouteSub = new Subject<RoutePlanning>();

  /** Fired when user picks an address from the map overlay. Forms subscribe to patch controls. */
  mapAddressPicked$ = new Subject<{ field: string; address: Address }>();

  // Add a BehaviorSubject for tracking the loading state
  private _loadingState = new BehaviorSubject<boolean>(false);

  private _mapService: MapService;

  constructor(
    private readonly routePlanningRepository: RoutePlanningRepository,
    private readonly queryParamsService: QueryParamsService,
    private readonly languageService: LanguageService,
    private readonly routeTrackingService: RouteTrackingService
  ) {}
  ngOnInit(): void {}

  getRoutesFromApi(
    body: GetRouteRequest
  ): Observable<ServiceResponse<RoutePlanning[]>> {
    return this.routePlanningRepository.getRoutesFromApi(body);
  }

  getAgencyContactInfo(
    agencyId: number
  ): Observable<ServiceResponse<AgencyContactInfo>> {
    return this.routePlanningRepository.getAgencyContactInfo(agencyId);
  }

  addRouteToCalendar(
    route: RoutePlanning,
    email: string,
    originName: string,
    destinationName: string
  ): Observable<ServiceResponse<string>> {
    return this.routePlanningRepository.addRouteToCalendar(
      route, 
      email, 
      originName, 
      destinationName
    );
  }

  // TODO: Move to pipe
  calcSectionDuration(duration: number) {
    const language = this.languageService.getSelectedLanguage();
    const travelDuration: string = TIME_UNITS.filter(
      (unit) => unit.value > eTimeUnits.SECOND
    )
      .sort((timeUnit, otherTimeUnit) => otherTimeUnit.value - timeUnit.value)
      .reduce((durationStr, timeUnit) => {
        const durationInTimeUnit: number = Math.floor(
          duration / timeUnit.value
        );

        if (durationInTimeUnit > 0) {
          let timeUnitLabel: string;
          switch (timeUnit.label) {
            case MINUTES.hebrew:
              timeUnitLabel = MINUTES[language.language];
              break;
            case HOURS.hebrew:
              timeUnitLabel = HOURS[language.language];
              break;
            case DAYS.hebrew:
              timeUnitLabel = DAYS[language.language];
              break;
            default:
              timeUnitLabel = '';
          }
          durationStr.push(`${durationInTimeUnit} ${timeUnitLabel}`);
        }

        duration = duration % timeUnit.value;

        return durationStr;
      }, [])
      .join(', ');

    return travelDuration;
  }

  calcSectionDistance(distance: number) {
    return distance < 1000
      ? distance
      : Math.round((distance / 1000) * 100) / 100;
  }

  // TODO: Move to pipe
  calcTravelDuration(duration: number) {
    const language = this.languageService.getSelectedLanguage();
    const hours = Math.floor(duration / eTimeUnits.HOUR);
    const minutes = Math.floor(
      (duration % eTimeUnits.HOUR) / eTimeUnits.MINUTE
    );

    const hoursString = hours.toString().padStart(2, '0');
    const minutesString = minutes.toString().padStart(2, '0');

    if (hours > 0)
      return `${hoursString}:${minutesString} ${HOURS[language.language]}`;
    else return `${minutesString} ${MINUTES[language.language]}`;
  }

  // TODO: Move to pipe
  getDateTime(dateTime: DateTime): Date {
    const date = dateTime.date;
    const time = dateTime.time;

    // date.setHours(
    //   parseInt(time.substring(0, 1)),
    //   parseInt(time.substring(1, 2))
    // );
    return date;
  }

  // TODO: Move to pipe
  calcDateHour(date: Date | string) {
    if (!!date) {
      if (typeof date == 'string') date = new Date(date);

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return hours + ':' + minutes;
    }

    return '';
  }

  addAddressesToQueryParam(values: { queryParam: string; value: string }[]) {
    values.forEach((value) =>
      this.queryParamsService.setQueryParam(value.queryParam, value.value)
    );
  }

  /** Sets origin in query params (org + oll). Used when address is selected from init map. */
  setOriginInQueryParams(address: Address): void {
    if (!address?.coordinates) return;
    const label = address.addressDetails?.label ?? '';
    const oll = `${address.coordinates.lat}_${address.coordinates.lng}`;
    this.queryParamsService.setQueryParams({ org: label, oll });
    this.mapAddressPicked$.next({ field: 'origin', address });
  }

  /** Sets destination in query params (dest + dll). Used when address is selected from init map. */
  setDestinationInQueryParams(address: Address): void {
    if (!address?.coordinates) return;
    const label = address.addressDetails?.label ?? '';
    const dll = `${address.coordinates.lat}_${address.coordinates.lng}`;
    this.queryParamsService.setQueryParams({ dest: label, dll });
    this.mapAddressPicked$.next({ field: 'destination', address });
  }

  /** Sets stop in query params (stop + sll). Used when address is selected from init map (source-destination tab). */
  setStopInQueryParams(address: Address): void {
    if (!address?.coordinates) return;
    const label = address.addressDetails?.label ?? '';
    const sll = `${address.coordinates.lat}_${address.coordinates.lng}`;
    this.queryParamsService.setQueryParams({ stop: label, sll });
    this.mapAddressPicked$.next({ field: 'stop', address });
  }

  /** Sets leg in query params (leg + lll). Used when address is selected from init map (car-integrated tab). */
  setLegInQueryParams(address: Address): void {
    if (!address?.coordinates) return;
    const label = address.addressDetails?.label ?? '';
    const lll = `${address.coordinates.lat}_${address.coordinates.lng}`;
    this.queryParamsService.setQueryParams({
      leg: label,
      lll,
      stopOpen: '1',
      legOpt: eLegOptions.ADDRESS,
      tab: eTab.CAR_INTERGRATED,
    });
    this.mapAddressPicked$.next({ field: 'leg', address });
  }

  /** Switches the forms tab to source-destination via query param (existing sub in forms container will react). */
  setTabToSourceDestination(): void {
    this.queryParamsService.setQueryParam('tab', eTab.SOURCE_DESTINATION);
  }

  /**
   * Clears address query params except the one that was just picked.
   * keptTarget: 'leg' = car-integrated leg address (leg/lll); 'stop' = source-destination stop (stop/sll).
   */
  clearOtherAddressParams(keptTarget: 'origin' | 'destination' | 'stop' | 'leg'): void {
    const toClear: Record<string, null> = {};
    if (keptTarget !== 'origin') {
      toClear['org'] = null;
      toClear['oll'] = null;
    }
    if (keptTarget !== 'destination') {
      toClear['dest'] = null;
      toClear['dll'] = null;
    }
    if (keptTarget !== 'stop') {
      toClear['stop'] = null;
      toClear['sll'] = null;
    }
    if (keptTarget !== 'leg') {
      toClear['leg'] = null;
      toClear['lll'] = null;
    }
    if (keptTarget !== 'stop' && keptTarget !== 'leg') {
      toClear['stopOpen'] = null;
    }
    this.queryParamsService.setQueryParams(toClear);
  }

  /** Current tab from query params. */
  getCurrentTab(): string | undefined {
    return this.queryParamsService.currentParams['tab'] as string | undefined;
  }

  getLanguageForRoute(): string {
    const language = this.languageService.getSelectedLanguage();
    switch (language.language) {
      case HEBREW.english:
        return 'he';
      case ENGLISH.english:
        return 'en';
      case ARABIC.english:
        return 'ar';
      default:
        return 'he';
    }
  }

  getArrival(
    selectedTimeOpt: eDepartureTimeOptions,
    formValues: { arrival?: DateTime }
  ): Date {
    if (selectedTimeOpt != eDepartureTimeOptions.ARRIVAL) {
      return null;
    } else {
      return formValues.arrival != null
        ? this.getDateTime(formValues.arrival)
        : null;
    }
  }

  getDeparture(
    selectedTimeOpt: eDepartureTimeOptions,
    formValues: { departure?: DateTime }
  ): Date {
    if (selectedTimeOpt === eDepartureTimeOptions.ARRIVAL) {
      return null;
    } else if (selectedTimeOpt === eDepartureTimeOptions.NOW) {
      return new Date();
    } else {
      return formValues.departure != null
        ? this.getDateTime(formValues.departure)
        : null;
    }
  }

  displayStopName = (stop: Stop) => {
    const language = this.languageService.getSelectedLanguage();
    return !!stop ? `${stop.name[language.symbol] || stop.name.he || ''} - ${stop.stopcode}` : '';
  }

  displayStopDetails(stop: Stop) {
    return !!stop ? `${stop.name} - ${stop.desc || stop.stopcode}` : '';
  }

  displayAddressName(address: Address) {
    return !!address &&
      !!address.addressDetails &&
      !!address.addressDetails.label
      ? address.addressDetails.label
      : '';
  }

  displayLineName = (line: Route) => {
    return !!line ? `${line.routeName} ${line.cityName}` : '';
  };

  displayCityName(city: City) {
    return city?.cityName ?? '';
  }

  displayPoiName(poi: PointOfInterest) {
    return poi?.name ?? '';
  }

  setTransportations(
    form: CarIntegratedForm | SourceDestinationForm
  ): eTransportationOptions[] {
    if (!form.options && !form.options.transportations) {
      return [];
    }

    const transportations = [];

    form.options.transportations.forEach((transport) => {
      switch (transport) {
        case eTransportationOptions.BUS:
          transportations.push(transport, "privateBus");
          break;
        case eTransportationOptions.TRAIN:
          transportations.push('regionalTrain');
          break;
        case eTransportationOptions.RAHBALIT:
        case eTransportationOptions.CARMELIT:
        case eTransportationOptions.LIGHT_TRAIN:
          if (!transportations.includes(eTransportationOptions.LIGHT_TRAIN)) {
            transportations.push(eTransportationOptions.LIGHT_TRAIN);
          }
          break;
      }
    });
    return transportations;
  }

  setMapService(mapService: MapService): void {
    this._mapService = mapService;
  }

  getMapService(): MapService {
    return this._mapService;
  }

  // Method to update the loading state
  updateLoadingState(isLoading: boolean): void {
    this._loadingState.next(isLoading);
  }

  // Method to get the loading state as an Observable
  getLoadingState(): Observable<boolean> {
    return this._loadingState.asObservable();
  }

  /**
   * Adds a route identifier to the URL as a query parameter
   * @param routeIdentifier The unique identifier for the route
   */
  addRouteIdentifierToUrl(routeIdentifier: string): void {
    console.debug('Setting routeId query parameter:', routeIdentifier);
    this.queryParamsService.setQueryParam('routeId', routeIdentifier);
  }

  /**
   * Clears the route identifier from the URL
   */
  clearRouteIdentifier(): void {
    console.debug('Clearing routeId query parameter');
    this.queryParamsService.setQueryParam('routeId', null);
  }

  /**
   * Selects a route programmatically
   * @param route The route to select
   */
  selectRoute(route: RoutePlanning): void {
    console.debug('Programmatically selecting route:', route);
    this._resultSubject.next({
      type: 'selected',
      value: route,
    });
  }

  startTrackingRoute(route: RoutePlanning): void {
    this._trackingRouteSub.next(route);
    this.routeTrackingService.startTracking(route);
  }

  stopTrackingRoute(): void {
    this.routeTrackingService.stopTracking();
    this.routeTrackingService.shownNotifications.clear();
  }

  closeOverlay() {
    this._componentOverlaySub.next({
      componentType: null,
      data: null
    });
  }
}
